import { BackendConfig, validateBackendConfig } from './BackendConfig';

let cachedConfig: BackendConfig | null = null;

/**
 * Loads platform configuration from environment variables
 */
export function loadPlatformConfig(): BackendConfig {
  if (cachedConfig) {
    return cachedConfig;
  }
  
  const backendType = import.meta.env.VITE_BACKEND_TYPE || 'aws';
  
  if (backendType === 'aws') {
    cachedConfig = validateBackendConfig({
      type: 'aws',
      auth: {
        /* configs are read from Amplify json anyways */
      },
      data: {
        graphqlEndpoint: import.meta.env.VITE_AWS_APPSYNC_ENDPOINT || '',
        region: import.meta.env.VITE_AWS_APPSYNC_REGION || 'eu-north-1',
      },
      storage: {
        bucket: import.meta.env.VITE_AWS_S3_BUCKET || '',
        region: import.meta.env.VITE_AWS_S3_REGION || 'eu-north-1',
      },
      events: {
        snsTopicArn: import.meta.env.VITE_AWS_SNS_TOPIC_ARN,
        sqsQueueUrl: import.meta.env.VITE_AWS_SQS_QUEUE_URL,
      },
    });
    return cachedConfig;
  }
  
  if (backendType === 'mock') {
    cachedConfig = validateBackendConfig({
      type: 'mock',
      auth: {},
      data: {},
      storage: {},
      events: {},
    });
    return cachedConfig;
  }

  if (backendType === 'standalone') {
    cachedConfig = validateBackendConfig({
      type: 'standalone',
      auth: {
        jwtSecret: import.meta.env.VITE_STANDALONE_JWT_SECRET || '',
        tokenExpiry: import.meta.env.VITE_STANDALONE_TOKEN_EXPIRY || '24h',
      },
      data: {
        graphqlEndpoint: import.meta.env.VITE_STANDALONE_GRAPHQL_ENDPOINT || '',
        postgresUrl: import.meta.env.VITE_STANDALONE_POSTGRES_URL || '',
      },
      storage: {
        type: (import.meta.env.VITE_STORAGE_PROVIDER as any) || 'local',
        config: {
          // Local storage config
          basePath: import.meta.env.VITE_STORAGE_LOCAL_PATH || './data/uploads',
          // MinIO config
          endpoint: import.meta.env.VITE_STORAGE_MINIO_ENDPOINT,
          accessKey: import.meta.env.VITE_STORAGE_MINIO_ACCESS_KEY,
          secretKey: import.meta.env.VITE_STORAGE_MINIO_SECRET_KEY,
          bucket: import.meta.env.VITE_STORAGE_MINIO_BUCKET,
          // S3-compatible config
          s3Endpoint: import.meta.env.VITE_STORAGE_S3_ENDPOINT,
          s3AccessKey: import.meta.env.VITE_STORAGE_S3_ACCESS_KEY,
          s3SecretKey: import.meta.env.VITE_STORAGE_S3_SECRET_KEY,
          s3Bucket: import.meta.env.VITE_STORAGE_S3_BUCKET,
        },
      },
      events: {
        redisUrl: import.meta.env.VITE_STANDALONE_REDIS_URL,
        jobQueueConfig: {
          concurrency: parseInt(import.meta.env.VITE_JOB_QUEUE_CONCURRENCY || '5'),
          retryAttempts: parseInt(import.meta.env.VITE_JOB_QUEUE_RETRY_ATTEMPTS || '3'),
        },
      },
    });
    return cachedConfig;
  }
  
  throw new Error(`Unsupported backend type: ${backendType}`);
}

/**
 * Validates that all required environment variables are present for the current backend type
 */
export function validateEnvironmentConfig(): void {
  try {
    loadPlatformConfig();
  } catch (error) {
    console.error('Platform configuration validation failed:', error);
    throw error;
  }
}
